import {
  html,
  css,
  ImageAPI,
  OmniElement,
  OmniIcon,
  OmniStyle,
  OmniTooltip,
  OmniImageInput,
} from 'omni-ui';

import { formComponent } from './form-component.js';
import { om2alert } from './om2-alert.js';

OmniIcon.register();
OmniStyle.register();
OmniImageInput.register();
OmniTooltip.register();

const DEFAULT_MAX_SIZE = 1024 ** 2;

export class Om2ImageUpload extends OmniElement {
  static get properties() {
    return {
      name: { type: String },
      value: { type: String },
      env: { type: String },
      ANsid: { type: String },
      circleDesign: { type: Boolean },
      loading: { attribute: false },
      placeholderText: { type: String },
      maxSize: { type: Number },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        /* Without this, long names can force this component to overflow horizontally */
        omni-img-input::part(filename) {
          overflow-wrap: anywhere !important;
        }
      `,
    ];
  }

  static register(name = 'om2-image-upload') {
    super.register(`${name}-impl`, Om2ImageUpload);
    return super.register(
      name,
      formComponent(Om2ImageUpload, { styles: { display: 'inline-flex' } })
    );
  }

  constructor() {
    super();
    this.placeholderText = 'Use default image';
  }

  _getANsid() {
    return this.ANsid ?? window?.AN?.sso?.session?.sid;
  }

  _setURL(value) {
    this.value = value;
    this.dispatchEvent(
      new CustomEvent('change', { detail: { value: this.value } })
    );
  }

  _fileName() {
    if (!this.value) return undefined;
    const match = this.value.match(/^.*?#(.+)$/);
    return match ? decodeURI(match[1]) : this.value;
  }

  async _setImage(e) {
    e.stopPropagation();
    const imgInputElement = e.target;
    const file = e?.detail?.file;
    if (!imgInputElement.reportValidity() || !file) {
      this._setURL(undefined);
      return;
    }

    try {
      const ansid = this._getANsid();
      if (!ansid) throw new Error("Missing ANsid: can't upload images");
      this.loading = true;
      this.dispatchEvent(new CustomEvent('loading-start'));
      const url = await new ImageAPI(ansid, this.env).createImageURL(file);
      this._setURL(url);
    } catch (e2) {
      if (e2.message) {
        om2alert(e2.message);
      } else {
        try {
          const error = await e2.json();
          om2alert(error.message);
        } catch {
          om2alert('Image upload failed');
        }
      }
      this._reset();
    } finally {
      if (this.loading) {
        this.loading = false;
        this.dispatchEvent(new CustomEvent('loading-end'));
      }
    }
  }

  _reset() {
    // Reset state of inner image upload component, despite their different APIs.
    const innerInput = this.shadowRoot.querySelector(
      'omni-img-input, omni-img-upload'
    );
    if (innerInput.reset) innerInput.reset();
    else {
      // Try to reset the component manually
      if (innerInput.imgSrc) innerInput.imgSrc = this.value;
      innerInput.shadowRoot.querySelectorAll('input').forEach(elem => {
        elem.value = ''; // eslint-disable-line no-param-reassign
      });
    }
  }

  _renderStandardDesign() {
    let mbDisplay = '1';

    if (this.maxSize) {
      const mbValue = this.maxSize / 1024 ** 2;
      mbDisplay = this.maxSize % 1024 ? mbValue.toFixed(1) : mbValue;
    }

    return html`
      <omni-img-input
        previewable
        class="${this.loading ? 'skeleton' : ''}"
        maxSize="${this.maxSize || DEFAULT_MAX_SIZE}"
        .filename=${this._fileName()}
        .previewSrc=${this.value}
        @change=${this._setImage}
      >
        <p slot="placeholder">${this.placeholderText}</p>
        <p slot="help">${mbDisplay}MB Max</p>
      </omni-img-input>
    `;
  }

  _renderCircleDesign() {
    return html`
      <div style="display:inline-flex">
        <omni-img-upload
          class="${this.loading ? 'skeleton' : ''}"
          img="${this.value}"
          maxSize="${this.maxSize || DEFAULT_MAX_SIZE}"
          @change=${this._setImage}
        >
        </omni-img-upload>
      </div>
    `;
  }

  render() {
    return html`
      <omni-style>
        ${this.circleDesign
          ? this._renderCircleDesign()
          : this._renderStandardDesign()}
      </omni-style>
    `;
  }
}

Om2ImageUpload.register();
